import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import type { UnitsPerScreen } from '../../../stores/controllers/siddur.controller';

/**
 * How much a reader takes at once: one unit, two or three. A beginner reads
 * one; someone who knows the prayer reads three and keeps the flow.
 *
 * Numbered rather than spelled out: יחידה / שתיים / שלוש read as three
 * unrelated words at a glance, where 1 2 3 read as one scale. The group is
 * named above it so the numbers mean something on their own.
 *
 * One control, two homes: the hamburger menu on every screen, and the side
 * panel beside the text where a desktop has the room. Both drive the same
 * store field, which the store keeps on the device.
 */
const SiddurUnitsControl = () => {
  const { t } = useTranslation();
  const { unitsPerScreen, setUnitsPerScreen, isReading } = useStoreByName('siddurStore');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography variant="body2">{t('siddurUnitsPerScreen')}</Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        disabled={isReading}
        value={unitsPerScreen}
        onChange={(_event, value: UnitsPerScreen | null) => value && setUnitsPerScreen(value)}
        aria-label={t('siddurUnitsPerScreen')}
        sx={{ '& .MuiToggleButton-root': { minWidth: 44, px: 1.5, fontVariantNumeric: 'tabular-nums' } }}
      >
        {([1, 2, 3] as UnitsPerScreen[]).map((count) => (
          <ToggleButton key={count} value={count} aria-label={`${count} ${t('siddurUnits')}`}>
            {count}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

export default observer(SiddurUnitsControl);
